const isOverOneMillion = (price: number) => price >= 1000000;
const isOverOneThousand = (price: number) => price >= 1000;

const getPostfix = (price: number) => {
    if (isOverOneMillion(price)) {
        return 'm';
    } else if (isOverOneThousand(price)) {
        return 'k';
    } else {
        return '';
    }
};

const getNumberToDivide = (price: number) => {
    if (isOverOneMillion(price)) {
        return 1000000;
    } else if (isOverOneThousand(price)) {
        return 1000;
    } else {
        return 1;
    }
};

const roundToTwoDecimalPlaces = (price: number) => {
    return Math.round((price + Number.EPSILON) * 10) / 10;
};

const getPriceDisplay = (
    currencySign: string,
    price: number,
    numberToDivide: number,
    postfix: string
) => {
    return `${currencySign}${roundToTwoDecimalPlaces(
        price / numberToDivide
    )}${postfix}`;
};

const getButtonTitle = (
    priceMin: number,
    priceMax: number,
    priceMinToDisplay: string,
    priceMaxToDisplay: string,
    defaultLabel: string,
    frequencySign?: string
) => {
    const minIsGreaterThanMax = priceMin > 0 && priceMax === 0;
    const maxIsGreaterThanMin = priceMax > 0 && priceMin === 0;

    const priceMinAndMaxAreAboveZero = priceMin > 0 && priceMax > 0;
    const priceMinAreZero = priceMin === 0 && priceMax === 0;

    if (minIsGreaterThanMax) {
        return `${priceMinToDisplay} min${
            frequencySign ? ` (${frequencySign})` : ''
        }`;
    } else if (maxIsGreaterThanMin) {
        return `${priceMaxToDisplay} max${
            frequencySign ? ` (${frequencySign})` : ''
        }`;
    } else if (priceMinAndMaxAreAboveZero) {
        return `${priceMinToDisplay} - ${priceMaxToDisplay}${
            frequencySign ? ` (${frequencySign})` : ''
        }`;
    } else if (priceMinAreZero) {
        return defaultLabel;
    }
};

const buildSelectPriceTitle = (
    priceMinSelectedValue: string,
    priceMaxSelectedValue: string,
    frequencySign?: string
) => {
    const priceMinInt = parseInt(priceMinSelectedValue || '0', 10);
    const priceMaxInt = parseInt(priceMaxSelectedValue || '0', 10);

    const currencySign = '£';
    const defaultLabel = 'Any price';

    const numberPostfixMin = getPostfix(priceMinInt);
    const numberPostfixMax = getPostfix(priceMaxInt);
    const numberToDivideMin = getNumberToDivide(priceMinInt);
    const numberToDivideMax = getNumberToDivide(priceMaxInt);

    const priceMinToDisplay = getPriceDisplay(
        currencySign,
        priceMinInt,
        numberToDivideMin,
        numberPostfixMin
    );
    const priceMaxToDisplay = getPriceDisplay(
        currencySign,
        priceMaxInt,
        numberToDivideMax,
        numberPostfixMax
    );

    const buttonTitle = getButtonTitle(
        priceMinInt,
        priceMaxInt,
        priceMinToDisplay,
        priceMaxToDisplay,
        defaultLabel,
        frequencySign
    );

    return buttonTitle || defaultLabel;
};

export const buildSelectPriceTitleToRent = (
    priceMinWeeklySelectedValue: string,
    priceMaxWeeklySelectedValue: string,
    priceMinMonthlySelectedValue: string,
    priceMaxMonthlySelectedValue: string,
    priceFrequencySelectedValue: string
) => {
    const defaultLabel = 'Any price';

    const buttonTitleMonthly = buildSelectPriceTitle(
        priceMinMonthlySelectedValue,
        priceMaxMonthlySelectedValue,
        'pcm'
    );

    const buttonTitleWeekly = buildSelectPriceTitle(
        priceMinWeeklySelectedValue,
        priceMaxWeeklySelectedValue,
        'pw'
    );

    const buttonText =
        priceFrequencySelectedValue !== 'per_week'
            ? buttonTitleMonthly
            : buttonTitleWeekly;

    return buttonText || defaultLabel;
};

export const buildSelectPriceTitleForSale = (
    priceMinSelectedValue: string,
    priceMaxSelectedValue: string
) => {
    return buildSelectPriceTitle(priceMinSelectedValue, priceMaxSelectedValue);
};
